import { AlertCircle, CheckCircle, RefreshCw, UserPlus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import influencerService from '../../services/influencerService';
import api from '../../utils/apiClient';
import LoadingSpinner from '../ui/LoadingSpinner';
import Modal from '../ui/Modal';

export default function ImportFromDMsModal({ onClose, onImported }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState({}); // { [ig_username]: 'pending' | 'done' | 'error' }

  const fetchContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/api/admin/posts/instagram/dm-contacts');
      setContacts(data.contacts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async (contact) => {
    setAdding((prev) => ({ ...prev, [contact.ig_username]: 'pending' }));
    try {
      await influencerService.createInfluencer({
        name: contact.name || contact.ig_username,
        ig_username: contact.ig_username,
        outreach_status: 'reached_out',
        payment_status: 'not_applicable',
      });
      setAdding((prev) => ({ ...prev, [contact.ig_username]: 'done' }));
      showToast(`@${contact.ig_username} added`, 'success');
      if (onImported) onImported();
    } catch (err) {
      setAdding((prev) => ({ ...prev, [contact.ig_username]: 'error' }));
      showToast(err.message, 'error');
    }
  };

  const addedCount = Object.values(adding).filter((v) => v === 'done').length;

  return (
    <Modal maxWidth="lg" closeOnBackdrop={false} onClose={onClose} className="rounded-xl shadow-2xl flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
        <div>
          <h2 className="text-lg font-semibold">Import from Instagram DMs</h2>
          <p className="text-sm text-muted-foreground">Add influencers who've messaged you on Instagram</p>
        </div>
        <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground">
          <X size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading ? (
          <div className="flex flex-col items-center py-10 gap-3 text-muted-foreground">
            <LoadingSpinner />
            <p className="text-sm">Fetching conversations…</p>
          </div>
        ) : error ? (
          <div className="py-8 text-center space-y-3">
            <AlertCircle size={36} className="mx-auto text-red-400" />
            <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>
            {error.toLowerCase().includes('permission') && (
              <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                instagram_manage_messages permission is required. Use "Check Instagram API" on this page to verify your
                token's permissions.
              </p>
            )}
            <button
              onClick={fetchContacts}
              className="flex items-center gap-2 mx-auto px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
            >
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        ) : contacts.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground/70">
            <p className="text-sm">No DM contacts found.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {addedCount > 0 && (
              <p className="text-xs text-green-600 dark:text-green-400 font-medium mb-3">
                {addedCount} contact{addedCount > 1 ? 's' : ''} added so far
              </p>
            )}
            {contacts.map((contact) => {
              const status = adding[contact.ig_username];
              return (
                <div key={contact.ig_username} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {contact.profile_pic ? (
                      <img src={contact.profile_pic} alt="" className="h-9 w-9 rounded-full object-cover" />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center text-sm font-bold text-muted-foreground">
                        {(contact.name || contact.ig_username).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-foreground">{contact.name || contact.ig_username}</p>
                      <p className="text-xs text-muted-foreground">@{contact.ig_username}</p>
                    </div>
                  </div>

                  {status === 'done' ? (
                    <CheckCircle size={18} className="text-green-500 flex-shrink-0" />
                  ) : status === 'pending' ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <button
                      onClick={() => handleAdd(contact)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                    >
                      <UserPlus size={12} /> Add
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t flex-shrink-0 flex justify-between items-center">
        <p className="text-xs text-muted-foreground/70">
          {contacts.length} contact{contacts.length !== 1 ? 's' : ''} found
        </p>
        <button onClick={onClose} className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50">
          Close
        </button>
      </div>
    </Modal>
  );
}
